/**
 * Visitor-facing plat / subdivision name.
 *
 * MLS SubdivisionName is an ingest key. Abbreviations (Oww, DrrhTrs, Bbr,
 * StoneTH, Crr 1) are not place names. Withhold them. Do not invent an
 * expansion.
 *
 * A recorded truncation (MLS "Triple" for Tetherow Triple Knot) may use the
 * independently confirmed visitor name. Do not invent expansions for codes
 * without a recorded full form.
 *
 * Founding case: /subdivisions/river-meadows More areas printed Oww,
 * DrrhTrs, Drrh Trs, OWW2 (fleet ca552556c46f87dbefdbe4ae948f1b68).
 * Triple: /subdivisions/triple printed the cut MLS token (walker-desktop 2026-08-19).
 */

import { displaySubdivision } from '@/lib/slug'

// ---------------------------------------------------------------------------
// Recording residue — the county's filing marks, never part of a place name.
// ---------------------------------------------------------------------------

/**
 * The county and city land-use file numbers that ride on the end of recent
 * plat labels. They are how the recorder finds the file, and no reader searches
 * them: "Canyon Ridge, Phase 4 711-18-000032-sub", "Easton Phase I
 * Plld20200979", "Petrosa Phase 5a Pz-20-0235", "Sunset Meadows Phases 1 And 2
 * Sub 22-01".
 *
 * The plat-family base rule (lib/market/plat-family.ts) strips exactly these,
 * so this list is the family rule's input too: widen it and the family lock
 * test re-derives every family. Display-only residue goes in
 * PLAT_DISPLAY_RESIDUE_RES below instead.
 */
const DOCUMENT_TOKEN_RES: readonly RegExp[] = [
  // Redmond / Deschutes County land-use numbers: 711-21-000260-sub,
  // 247-22-000182-tp, 711-21-000185-plng-sub, 247-25-00291-tp.
  /\b\d{3}-\d{2}-\d{3,6}(?:-[a-z]+)*\b/gi,
  // City of Bend land-use numbers: Plld20200979, PLLD 20211070.
  /\bplld\s?\d{6,}\b/gi,
  // Pz-20-0235, Pz20-0183, Pz 20-0027.
  /\bpz[-\s]?\d{2}-\d{3,4}\b/gi,
  // Sisters / La Pine file numbers: Sub 22-01, Sub-21-01, Mod 20-02/sub20-01.
  /\bmod\s+\d{2}-\d{2}(?:\/\s*sub\s?\d{2}-\d{2})?/gi,
  /\bsub[-\s]?\d{2}-\d{2}\b/gi,
  // Minor partition numbers: Mp-80-93.
  /\bmp-\d{2}-\d{2,3}\b/gi,
  // County survey cross-references: See Cs06623, Cs06506, Aff Cor.
  /\bsee\s+cs\s*\d+\b/gi,
  /\bcs\s?\d{4,}\b/gi,
  /\baff\.?\s+cor\.?/gi,
  // Plat. No. 2002-61.
  /\bplat\.?\s+no\.?\s+\d{4}-\d+\b/gi,
]

/**
 * Filing marks a visitor must never read, removed for DISPLAY only (Muse site
 * audit 2026-10-20, W7: /subdivisions printed 21 "plng", "Drrh Trs", "Owen
 * Ridge Phases 1 and 2 plng Sub", "River Village Condominiums Supplementalal
 * Plat"). Slugs and URLs never pass through here.
 *
 *   - "plng": the planning-file suffix a stripped file number leaves behind;
 *   - "Trs": the MLS tracts abbreviation ("Drrh Trs");
 *   - a trailing "Sub", "Subd", "Sub'd": the filing type, not the name;
 *   - "(First) Supplemental Plat", including the county's "Supplementalal",
 *     and a trailing bare "Supplemental";
 *   - a trailing bare "Plat";
 *   - a county file code the numeric patterns above miss: "01qp-02-sub-19".
 *
 * Nothing is expanded or renamed: words are only removed.
 */
const PLAT_DISPLAY_RESIDUE_RES: readonly RegExp[] = [
  /\b\d+[a-z]*-\d+-sub-\d+\b/gi,
  /\bplng\b/gi,
  /\btrs\b\.?/gi,
  /\b(?:(?:first|second|third|\d+(?:st|nd|rd|th))\s+)?supplement(?:al)*\.?\s+plat\b/gi,
  /\bsupplement(?:al)*\.?\s*$/i,
  /\bsub(?:'?d)?\.?\s*$/i,
  /\bplat\.?\s*$/i,
]

function tidy(value: string): string {
  return value
    .replace(/\s+([,.])/g, '$1')
    .replace(/\s{2,}/g, ' ')
    .replace(/^[\s,.&/-]+|[\s,&/-]+$/g, '')
    .trim()
}

/** tidy, plus the doubled comma a mid-label removal leaves ("Foo, , Phase 2"). */
function tidyResidue(value: string): string {
  return tidy(value.replace(/,(?:\s*,)+/g, ','))
}

/** A county label with its land-use file numbers removed, otherwise untouched. */
export function stripCountyDocumentTokens(label: string): string {
  let out = label
  for (const re of DOCUMENT_TOKEN_RES) out = out.replace(re, ' ')
  return tidy(out)
}

/**
 * THE display humanizer for a plat or subdivision name: county file numbers
 * and every filing mark in PLAT_DISPLAY_RESIDUE_RES out, the words of the
 * name kept in order. "Antler Meadows 711-24-000089-plng" reads "Antler
 * Meadows"; "Owen Ridge Phases 1 And 2 711-22-000209-plng Sub" reads "Owen
 * Ridge Phases 1 And 2". Phase numbers stay: they are how two recorded phases
 * of one subdivision tell apart. Returns '' when nothing of a name is left.
 */
export function stripPlatRecordingResidue(name: string): string {
  let out = stripCountyDocumentTokens(name)
  // Twice: removing "711-…-plng" can expose a trailing "Sub" that was not at
  // the end before.
  for (let pass = 0; pass < 2; pass += 1) {
    for (const re of PLAT_DISPLAY_RESIDUE_RES) out = tidyResidue(out.replace(re, ' '))
  }
  // A dangling connector left at the end by a removal ("Foo And Sub").
  return tidyResidue(out.replace(/\s+(?:of|and|&)$/i, ''))
}

/** MLS ingest token → recorded visitor name. Keys are lowercased. */
const RECORDED_PLAT_DISPLAY: Record<string, string> = {
  triple: 'Triple Knot',
  // Deschutes County records the plat as "Farm (the)" (CSNUM 07193), which
  // title-cases out of the slug as "Farm The". MLS calls it "The Farm". Both
  // spellings map to the recorded visitor name — this is a reordering of the
  // county's own words, not an invented expansion.
  'farm the': 'The Farm',
  'farm (the)': 'The Farm',
  // W7 follow-up (Public Look, 2026-10-21): the feed writes Crooked River Ranch
  // as "Crr" (equivalence recorded in lib/data/geo/report-cities.ts and
  // app/zip/[zip]/_v3/zip-constants.ts; the listing breadcrumb already reads it).
  crr: 'Crooked River Ranch',
  // "PLA" is the feed's code for the recorded Prineville Lake Acres plat
  // (lib/market/__fixtures__/plat-family-recorded.json), and the feed also
  // writes the run-together "PrinevilleLakeAcres".
  pla: 'Prineville Lake Acres',
  prinevillelakeacres: 'Prineville Lake Acres',
  'prinevillelakeacres 1': 'Prineville Lake Acres 1',
  'prinevillelakeacres 2': 'Prineville Lake Acres 2',
}

const KNOWN_MLS_ABBREVIATIONS = new Set(
  [
    'oww',
    'oww2',
    'drrhtrs',
    'drrh trs',
    'bbr',
    'stoneth',
    'crr 1',
    'crr1',
    'crr',
    'olu',
    'sfr',
  ].map((s) => s.toLowerCase()),
)

/** MLS property-type / village tokens that leak into visitor doors. */
const MLS_DUMP_TOKEN_RE = /\b(sfr|olu)\b/i

/**
 * Letter-coded MLS phases (Phase C-2, Phase C1, Phase A, Phase D).
 * Numbered English phases stay: Phase 1, Phase Three, Phase Twenty-two,
 * Phase RS-1 (two-letter recorded suffix, not a single-letter code).
 */
const MLS_LETTER_PHASE_RE = /\bphase\s+[a-z](?:-?\d+)?\b/i

/**
 * MLS field-length truncations that end a name mid-word. The MLS cuts
 * SubdivisionName at a fixed width, so a plat filed as "Deschutes River
 * Tract" or "Aspen Creek Mobile Park" arrives as "Deschutes River Trac" /
 * "Aspen Creek Mob Pk". The visitor name is not knowable from the token, so
 * the whole name is withheld rather than expanded (visibility audit
 * 2026-09-22, VOICE-3: /price-drops printed "Bend · Deschutes River Trac",
 * "Redmond · Aspen Creek Mob Pk", "Bend · Inn Of The 7th"). Readable
 * abbreviations (Mtn, Est, Addn, Sub) are left alone.
 */
const MLS_TRUNCATED_TAIL_RE = /\b(trac|tr|mob|pk|prk|mhpk|sec)$/i

/**
 * A name that stops on a bare ordinal ("Inn Of The 7th") is a truncation:
 * the noun the ordinal qualified was cut. "7th Mountain" and "Phase 3" keep
 * their noun or number and are untouched.
 */
const MLS_TRAILING_ORDINAL_RE = /\b\d+(st|nd|rd|th)$/i

/** Short letter tokens that read as words or recorded forms, not MLS codes. */
const NO_VOWEL_WORD_ALLOW = new Set(['mtn', 'mkt', 'blvd', 'hwy', 'mhc'])

/**
 * Two or more vowel-less words of three or more letters is an MLS code dump
 * ("Desc Rvr Hmst Rimrk"). One such word is usually a readable abbreviation
 * ("Mtn Village", "Reed Mkt East") and stays.
 */
function hasNoVowelCodeRun(name: string): boolean {
  const words = name.split(/[\s,-]+/).filter(Boolean)
  const codes = words.filter(
    (word) =>
      /^[A-Za-z]{3,6}$/.test(word) &&
      !NO_VOWEL_WORD_ALLOW.has(word.toLowerCase()) &&
      !/^[ivxlcdm]+$/i.test(word) &&
      (word.match(/[aeiouyAEIOUY]/g) ?? []).length === 0,
  )
  return codes.length >= 2
}

/**
 * City names the MLS sometimes stores with a stray interior capital
 * ("PrineVille - Fifth"). Recasing a city's own name is casing, not a rename.
 */
const CITY_CASING: Record<string, string> = {
  prineville: 'Prineville',
  redmond: 'Redmond',
  bend: 'Bend',
  sisters: 'Sisters',
  sunriver: 'Sunriver',
  terrebonne: 'Terrebonne',
  madras: 'Madras',
  tumalo: 'Tumalo',
}

function recaseCityWords(name: string): string {
  return name.replace(/[A-Za-z]+/g, (word) => {
    const fixed = CITY_CASING[word.toLowerCase()]
    return fixed && word !== fixed && /[a-z][A-Z]/.test(word) ? fixed : word
  })
}

function compactLetters(name: string): string {
  return name.replace(/[^A-Za-z0-9]/g, '')
}

export function looksLikeMlsAbbreviation(name: string): boolean {
  const trimmed = name.trim()
  if (!trimmed) return false
  if (KNOWN_MLS_ABBREVIATIONS.has(trimmed.toLowerCase())) return true
  if (MLS_DUMP_TOKEN_RE.test(trimmed)) return true
  if (MLS_LETTER_PHASE_RE.test(trimmed)) return true
  if (MLS_TRUNCATED_TAIL_RE.test(trimmed)) return true
  if (MLS_TRAILING_ORDINAL_RE.test(trimmed)) return true
  if (hasNoVowelCodeRun(trimmed)) return true
  if (/^[A-Z]{2,5}\d{0,2}$/.test(trimmed)) return true
  if (/^[A-Za-z]{2,4}\s+\d+$/.test(trimmed)) return true
  if (/\bVill\b/i.test(trimmed)) return true
  if (!/\s/.test(trimmed) && trimmed.length <= 8) {
    const vowels = (trimmed.match(/[aeiouAEIOU]/g) ?? []).length
    const hasInternalCap = /[a-z][A-Z]/.test(trimmed)
    if (vowels === 0) return true
    if (hasInternalCap) return true
    if (trimmed.length <= 3 && vowels <= 1) return true
  }
  const compact = compactLetters(trimmed)
  if (compact.length <= 8 && (compact.match(/[aeiouAEIOU]/g) ?? []).length === 0) {
    return true
  }
  return false
}

/**
 * MLS ingest label a visitor must never tap as a child door.
 * Withholds the whole string. Does not invent an expansion.
 */
export function looksLikeMlsFilingLabel(name: string): boolean {
  return looksLikeMlsAbbreviation(name)
}

/**
 * Words a place name does not capitalise in the middle of itself.
 *
 * WHY: MLS SubdivisionName capitalises every word, and so did the slug
 * fallback, so /subdivisions/ridge-at-eagle-crest published "Ridge At Eagle
 * Crest" — in the H1, the breadcrumb, the by-the-numbers heading, all five Q&A
 * questions, and the FAQPage JSON-LD Google reads (separate evaluator,
 * 2026-09-08). This is English title case, not a rename: no word is added,
 * removed or reordered, only cased the way the language cases it. It is
 * therefore NOT an invented expansion, which is what this module withholds.
 * One list, used by every path that builds a visitor plat name.
 */
export const TITLE_LOWER_WORDS: ReadonlySet<string> = new Set([
  'a',
  'an',
  'and',
  'at',
  'by',
  'de',
  'del',
  'for',
  'in',
  'la',
  'las',
  'los',
  'of',
  'on',
  'or',
  'the',
  'to',
  'van',
  'von',
])

/**
 * Lower an interior connector word. The first and last words always keep their
 * capital, so "The Ridge" keeps its The and a name is never left ending on a
 * dangling preposition. A word that is not exactly a connector is untouched,
 * including anything with internal capitals or digits, so MLS phase markers
 * (Phase 3, 7th) and camel tokens survive unchanged.
 */
export function titleCasePlaceName(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return name
  return words
    .map((word, i) => {
      if (i === 0 || i === words.length - 1) return word
      return TITLE_LOWER_WORDS.has(word.toLowerCase()) ? word.toLowerCase() : word
    })
    .join(' ')
}

/** A roman numeral of 2+ letters from I to XXXIX: the county's phase numbers. */
const ROMAN_PHASE_RE = /^(?=[ivx]{2,})x{0,3}(?:ix|iv|v?i{0,3})$/i

/**
 * The county types some roman phase numbers in title case ("Broken Top Phase
 * Ii-c", "Iv-f"). A phase number is a numeral, so it is cased as one: "Phase
 * II-C", "Phase IV-F". Only whole words that ARE a numeral change, so no
 * ordinary word is touched ("Vista", "Civic" and "Ivy" are not numerals).
 */
export function recaseRomanPhases(name: string): string {
  return name
    .split(' ')
    .map((token) => {
      // Trailing punctuation rides outside the numeral: "Xi," is "XI,".
      const [, word = token, trail = ''] = /^(.*?)([,.;:]*)$/.exec(token) ?? []
      const [head, ...rest] = word.split('-')
      if (!head || !ROMAN_PHASE_RE.test(head)) return token
      const suffix = rest.map((part) => (/^[a-z]\d?$/i.test(part) ? part.toUpperCase() : part))
      return [head.toUpperCase(), ...suffix].join('-') + trail
    })
    .join(' ')
}

/**
 * The county types a hyphen-joined name with the second half lowered ("Tri-peaks
 * III"); each half is a word, so each is capitalised ("Tri-Peaks III"). Only a
 * capitalised word joined to a lowercase alphabetic word changes (SITE-216).
 */
function recaseHyphenJoins(name: string): string {
  return name.replace(/\b([A-Z][a-z]+)-([a-z])([a-z]+)\b/g, (_, head, first, rest) => `${head}-${first.toUpperCase()}${rest}`)
}

export function publishPlatDisplayName(raw: string | null | undefined): string | null {
  const sentinelFree = displaySubdivision(raw)
  if (!sentinelFree) return null
  const recordedRaw = RECORDED_PLAT_DISPLAY[sentinelFree.toLowerCase()]
  if (recordedRaw) return recordedRaw
  // Recording residue out before anything else, so a file number never reaches
  // a visitor and never hides an abbreviation the test below would catch
  // ("Drrh Trs" is "Drrh" once "Trs" is gone, and "Drrh" is withheld).
  const cleaned = stripPlatRecordingResidue(sentinelFree)
  if (!cleaned) return null
  const recorded = RECORDED_PLAT_DISPLAY[cleaned.toLowerCase()]
  if (recorded) return recorded
  // The abbreviation test runs on the CLEANED name, before casing, because
  // casing must not turn a withheld token into a publishable one.
  if (looksLikeMlsFilingLabel(cleaned)) return null
  // A roman phase number is a numeral however the county typed it: every name
  // published here reads "Phase II", never "Phase Ii" (SITE-216).
  return recaseRomanPhases(recaseHyphenJoins(titleCasePlaceName(recaseCityWords(cleaned))))
}
